import React from 'react'
import cx from 'classnames'
import { Input as AntInput, Select } from 'antd'
import Button from 'components/Button'
import Icon from 'components/Icon'
import styles from './Input.scss'
import SearchChips, { SearchChip } from 'components/SearchChips/SearchChips'

const allowedTypes = ['text', 'number', 'search', 'select', 'checkbox', 'range', 'radio', 'password']
const [TYPE_TEXT, TYPE_NUMBER] = allowedTypes

const typeToIconName: { search: string; select: string } = {
  search: 'search',
  select: 'selectArrow',
}

export type OnRemoveChip = (clickedChip: SearchChip) => void

export interface IInput {
  type: 'text' | 'number' | 'search' | 'select' | 'checkbox' | 'range' | 'radio' | 'password'
  icon?: string | null
  prefix?: string
  suffix?: string
  hint?: string
  placeholder?: string
  allowDeselect?: boolean
  onChange: (...args: any[]) => void
  onFocusOrBlur?: () => void
  onBlur?: () => void
  onClear?: () => void
  children?: React.ReactNode
  className?: string
  wrapperClassName?: string
  id?: string
  name?: string
  checked?: boolean
  disabled?: boolean
  value?: string
  searchChips?: SearchChip[]
  onRemoveChip?: OnRemoveChip
  isHalfChecked?: boolean
  invalid?: boolean
}

export default function Input(props: IInput) {
  const {
    type = TYPE_TEXT,
    icon,
    prefix,
    suffix,
    hint,
    children,
    onChange = () => {},
    onClear,
    className,
    wrapperClassName,
    allowDeselect = false,
    searchChips,
    onRemoveChip,
    isHalfChecked,
    ...rest
  } = props

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    const nextValue = type === TYPE_NUMBER ? parseInt(value) : value

    onChange(nextValue, name)
  }

  const restProps = rest

  const iconNameByType: string | undefined = typeToIconName[type as keyof typeof typeToIconName]

  return (
    <div className={cx(styles.wrapper, wrapperClassName)}>
      {iconNameByType && <Icon name={iconNameByType} size='small' className={styles[`input-${type}-icon`]} />}
      {icon && <Icon name={icon} size='small' className={styles[`input-${icon}-icon`]} />}
      {prefix && <span className={styles.prefix}>{prefix}</span>}
      {type === 'select' ? (
        <Select
          {...restProps}
          value={restProps.value}
          onChange={(value) => onChange(value, restProps.name)}
          className={cx(className, styles.input, styles[`input-${type}`], styles[`input-${icon}`], {
            [styles.withPrefix]: !!prefix,
            [styles.deselect]: allowDeselect,
            [styles.halfChecked]: isHalfChecked,
          })}
          style={{ width: '100%' }}
        >
          {children}
        </Select>
      ) : (
        <AntInput
          {...restProps}
          type={type as any}
          onChange={onInputChange}
          className={cx(className, styles.input, styles[`input-${type}`], styles[`input-${icon}`], {
            [styles.withPrefix]: !!prefix,
            [styles.deselect]: allowDeselect,
            [styles.halfChecked]: isHalfChecked,
          })}
        />
      )}
      {searchChips && onRemoveChip && <SearchChips chips={searchChips} onRemoveChip={onRemoveChip} />}
      {onClear && (
        <Button buttonType='transparent' onClick={onClear} className={styles.clear}>
          <Icon name='close' size='small' />
        </Button>
      )}
      {hint && <span className={styles.hint}>{hint}</span>}
    </div>
  )
}
